import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {
  studentFormsApi,
  type StudentFormCatalogField,
  type StudentFormFieldAccess,
  type StudentFormFieldType,
} from '@/api/studentForms'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

const FIELD_TYPES: StudentFormFieldType[] = [
  'text',
  'textarea',
  'select',
  'multiselect',
  'date',
  'phone',
  'email',
  'url',
  'boolean',
  'rating',
  'language_pair',
]

const ACCESS_LEVELS: StudentFormFieldAccess[] = [
  'teacher_only',
  'student_visible',
  'student_editable',
]

interface StudentFormFieldDialogProps {
  open: boolean
  field: StudentFormCatalogField | null
  onClose: () => void
  onSaved: () => void
}

/**
 * Поле системного каталога анкеты (админ).
 *
 * Ключ не редактируется: он генерируется на бэкенде из названия, и от него
 * зависит связь с уже сохранёнными ответами учеников.
 */
export function StudentFormFieldDialog({
  open,
  field,
  onClose,
  onSaved,
}: StudentFormFieldDialogProps) {
  const { t } = useTranslation()

  const [label, setLabel] = useState('')
  const [type, setType] = useState<StudentFormFieldType>('text')
  const [optionsText, setOptionsText] = useState('')
  const [access, setAccess] = useState<StudentFormFieldAccess>('student_editable')
  const [required, setRequired] = useState(false)
  const [deletable, setDeletable] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLabel(field?.label ?? '')
    setType(field?.type ?? 'text')
    setOptionsText((field?.options ?? []).join('\n'))
    setAccess(field?.access ?? 'student_editable')
    setRequired(field?.required ?? false)
    setDeletable(field?.deletable ?? true)
    setError(null)
  }, [open, field])

  const needsOptions = type === 'select' || type === 'multiselect'

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const options = optionsText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)

      const payload = {
        label: label.trim(),
        type,
        access,
        required,
        deletable,
        ...(needsOptions ? { options } : {}),
      }

      if (field) {
        await studentFormsApi.admin.update(field.id, payload)
      } else {
        await studentFormsApi.admin.create(payload)
      }
      onSaved()
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('studentForms.saveFieldError')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {field ? t('studentForms.editField') : t('studentForms.createField')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('studentForms.fieldLabel')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            size="small"
            required
            helperText={
              field ? `${t('studentForms.fieldKey')}: ${field.key}` : t('studentForms.keyAutoHint')
            }
          />

          <FormControl size="small" fullWidth>
            <InputLabel id="student-form-field-type">{t('studentForms.fieldType')}</InputLabel>
            <Select
              labelId="student-form-field-type"
              label={t('studentForms.fieldType')}
              value={type}
              onChange={(e) => setType(e.target.value as StudentFormFieldType)}
            >
              {FIELD_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`studentForms.fieldTypes.${value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {needsOptions && (
            <TextField
              label={t('studentForms.fieldOptions')}
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
              helperText={t('studentForms.fieldOptionsHint')}
            />
          )}

          <FormControl size="small" fullWidth>
            <InputLabel id="student-form-field-access">{t('studentForms.fieldAccess')}</InputLabel>
            <Select
              labelId="student-form-field-access"
              label={t('studentForms.fieldAccess')}
              value={access}
              onChange={(e) => setAccess(e.target.value as StudentFormFieldAccess)}
            >
              {ACCESS_LEVELS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`studentForms.accessLevels.${value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <FormControlLabel
              control={
                <Checkbox checked={required} onChange={(e) => setRequired(e.target.checked)} />
              }
              label={t('studentForms.fieldRequiredAlways')}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
              {t('studentForms.fieldRequiredAlwaysHint')}
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox checked={deletable} onChange={(e) => setDeletable(e.target.checked)} />
              }
              label={t('studentForms.fieldDeletable')}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
              {t('studentForms.fieldDeletableHint')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={saving || label.trim() === '' || (needsOptions && optionsText.trim() === '')}
          onClick={() => void handleSave()}
        >
          {saving ? t('common.saving', 'Сохранение...') : t('common.save', 'Сохранить')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
